import { BlogPost } from '@/types/blog';
import coverImage from '@/assets/blog-ca-solar-permit-fees-cover.jpg';

const th = 'class="text-left p-3 text-sm font-semibold"';
const td = 'class="p-3 text-sm align-top border-b border-gray-200"';

export const post: BlogPost = {
  slug: 'california-450-solar-permit-fee-cap-2026',
  title: "California's $450 Solar Permit Fee Cap: What It Covers, and Where Nine Jurisdictions Landed for 2026/27",
  excerpt:
    "We pulled 2025/26 and 2026/27 residential solar and storage permit fees for nine California jurisdictions. The headline finding: the $450 statutory cap is behaving like a price rather than a ceiling.",
  content: `
    <p>Most California jurisdictions adopt new fee schedules at the start of the fiscal year, which makes July the month residential solar permit costs change.</p>

    <p>We pulled 2025/26 and 2026/27 residential solar and storage permit fees for nine California jurisdictions. The headline finding: the $450 statutory cap is behaving like a price rather than a ceiling.</p>

    <h2>What the cap actually says</h2>

    <p><a href="https://codes.findlaw.com/ca/government-code/gov-sect-66015/" target="_blank" rel="noopener noreferrer">Government Code 66015</a> limits residential photovoltaic permit fees to $450 for systems up to 15 kW, plus $15 per kW above 15 kW. Commercial systems cap at $1,000 up to 50 kW, plus $7 per kW between 51 and 250 kW, plus $5 per kW above 250 kW.</p>

    <p>Two details in the statute get missed regularly.</p>

    <p><strong>The cap covers the sum of all charges.</strong> Subdivision (e) defines "residential permit fee" as the sum of all charges a jurisdiction levies in connection with the application for a solar energy system installed on a single or two-family dwelling. Splitting a fee into plan check, inspection, and administrative line items does not create room under the cap. The total is what counts.</p>

    <p><strong>A jurisdiction can charge more, with paperwork.</strong> Subdivision (a)(2) permits fees above the cap where the jurisdiction adopts a written finding by resolution or ordinance, supported by substantial evidence of the reasonable cost to issue the permit. Subdivision (c) sets what the finding has to contain: a determination that the jurisdiction adopted streamlining ordinances and processes consistent with the California Solar Permitting Guidebook and model ordinances, a cost calculation that accounts for any reductions available under 65850.5, and a description of how the higher fee produces a quick and streamlined approval.</p>

    <p>Two related provisions matter for how fees appear on an invoice. Section 65850.55 bars pricing a solar permit off the value of the system or the property, and requires each fee to be identified separately on the invoice provided to the applicant. AB 1132 extended the fee caps from their original January 1, 2025 sunset through January 1, 2034.</p>

    <h2>The 2026 numbers</h2>

    <h3>Residential solar</h3>

    <div class="overflow-x-auto my-8 rounded-lg border border-gray-200">
      <table class="w-full border-collapse">
        <thead class="bg-fordje-navy text-white">
          <tr>
            <th ${th}>Jurisdiction</th>
            <th ${th}>2025/26 Solar Fees</th>
            <th ${th}>2026/27 Solar Fees</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td ${td}><strong>Fairfield</strong></td>
            <td ${td}>$72.74; $118.71 separate inspection fee &le;15 kW plus $10.50/kW above 15 kW</td>
            <td ${td}>Roof-mount: $157 &le;15 kW plus $10.50/kW above 15 kW<br />Ground-mount: $328 &le;15 kW plus $10.50/kW above 15 kW</td>
          </tr>
          <tr>
            <td ${td}><strong>Livermore</strong></td>
            <td ${td}>$440 &le;15 kW; $500 plus $15/kW above 15 kW</td>
            <td ${td}>$451</td>
          </tr>
          <tr>
            <td ${td}><strong>Solano County</strong></td>
            <td ${td}>Roof mount: $184<br />Ground mount: $553</td>
            <td ${td}>Roof mount: $194<br />Ground mount: $581</td>
          </tr>
          <tr>
            <td ${td}><strong>Richmond</strong></td>
            <td ${td}>$564</td>
            <td ${td}>$450</td>
          </tr>
          <tr>
            <td ${td}><strong>Novato</strong></td>
            <td ${td}>$323 &le;15 kW; plus $15/kW above 15 kW</td>
            <td ${td}>$330 for systems 15 kW or less; $15 per kW above 15 kW</td>
          </tr>
          <tr>
            <td ${td}><strong>Mendocino County</strong></td>
            <td ${td}>$450 &le;15 kW; plus $15/kW above 15 kW</td>
            <td ${td}>$450 &le;15 kW; plus $15/kW above 15 kW</td>
          </tr>
          <tr>
            <td ${td}><strong>Santa Rosa</strong></td>
            <td ${td}>$96.52 (residential roof-mounted system); $289.56 inspection charged separately</td>
            <td ${td}>$100.18 plan check + $300.56 inspection</td>
          </tr>
          <tr>
            <td ${td}><strong>San Ramon</strong></td>
            <td ${td}>Solar: $551 for &le;10 kW; plus $18/kW above 10 kW<br />SolarAPP: $450 (in-lieu of separate plan check fees)</td>
            <td ${td}>Solar: $566 for up to 10 kW, plus $17 per additional kW<br />SolarAPP: $450 (in-lieu of separate plan check fees)</td>
          </tr>
          <tr>
            <td ${td}><strong>Concord</strong></td>
            <td ${td}>Solar: $89.50 up to 15 kW, $2.56/kW up to 50 kW; $222 inspection fee<br />SolarAPP: $311.50 (in-lieu of plan check and inspection fees)</td>
            <td ${td}>Solar: $70 administrative fee; $115 plan review for systems up to 15 kW, plus $5 per kW above 15 kW; inspection base fee $265 + $10 per kW above 15 kW<br />SolarAPP: $70 administrative fee up to 38.4 kW max; inspection base fee $380 + $15 per kW above 15 kW</td>
          </tr>
        </tbody>
      </table>
    </div>

    <p>Range for 2026: $157 to $566, a 3.6x spread across nine jurisdictions operating under one statewide cap.</p>

    <p>These cover a set number of inspections that varies by jurisdiction. Additional inspections cost extra.</p>

    <h3>Residential storage, where the jurisdiction publishes a separate fee</h3>

    <div class="overflow-x-auto my-8 rounded-lg border border-gray-200">
      <table class="w-full border-collapse">
        <thead class="bg-fordje-navy text-white">
          <tr>
            <th ${th}>Jurisdiction</th>
            <th ${th}>2025/26 Storage Fees</th>
            <th ${th}>2026/27 Storage Fees</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td ${td}><strong>Fairfield</strong></td>
            <td ${td}>$36.37 (up to 49 kWh); $118.71 separate inspection fee</td>
            <td ${td}>$155 (up to 49 kWh)</td>
          </tr>
          <tr>
            <td ${td}><strong>Livermore</strong></td>
            <td ${td}>$143</td>
            <td ${td}>$147</td>
          </tr>
          <tr>
            <td ${td}><strong>Richmond</strong></td>
            <td ${td}>$192</td>
            <td ${td}>$200</td>
          </tr>
          <tr>
            <td ${td}><strong>Santa Rosa</strong></td>
            <td ${td}>$180.97</td>
            <td ${td}>$394.00 fire permit plan review and inspection + $187.85 electrical permit fee</td>
          </tr>
        </tbody>
      </table>
    </div>

    <p>Solano County, Novato, Mendocino County, San Ramon, and Concord do not publish a separate residential storage fee in either year.</p>

    <h2>Four patterns</h2>

    <h3>1. Convergence on $450</h3>

    <p>Four of the nine now sit on the cap or within a dollar of it, and they arrived from opposite directions. Richmond came down from $564. Concord went up from $311.50. Mendocino County held flat at $450 across both years. Livermore moved from $440 to $451.</p>

    <p>A statutory ceiling that jurisdictions converge on from below is functioning as an anchor. Cost-of-service pricing should produce scattered numbers, since inspection labor rates and plan review workloads genuinely differ across jurisdictions. What's happening instead is compression from both sides. The high jurisdictions are coming down, and the low ones are coming up to meet them.</p>

    <h3>2. Storage is absorbing the increases</h3>

    <p>Section 66015 caps solar. It says nothing about standalone battery storage permits, and the one big move this year happened there.</p>

    <p>Santa Rosa restructured residential storage from a single $180.97 charge into a $394.00 fire permit plan review and inspection plus a $187.85 electrical permit fee, a 221% increase in one cycle. Livermore and Richmond moved up 2.8% and 4.2%, in line with everything else on their schedules. Fairfield held flat at $155.</p>

    <p>Only four of the nine publish a residential storage fee at all, but changes abound. Watch this space for unexpected cost structure changes.</p>

    <h3>3. Cost recovery is shifting toward inspection</h3>

    <p>Three of the nine moved money toward the inspection line specifically.</p>

    <p>Solano County raised its residential ground mount fee from $553 to $581 based on inspection cost. Concord shifted its structure so inspection carries $265 of the $450, and added a $10 per kW escalator above 15 kW on inspection where the 2025 schedule applied $2.56 per kW to plan review. Santa Rosa's inspection charge runs roughly 3x its plan check.</p>

    <p>The statute explains why. Subdivision (d) defines administrative costs to include the review, approval, and issuance of the permit and the hourly site inspection and followup costs. Inspection is named in the statute as recoverable, it's billed hourly, and it maps cleanly to a timesheet and a headcount. A building department defending an inspection figure has a paper trail that survives scrutiny.</p>

    <p>Plan review is harder to defend, and getting harder. Subdivision (c)(2) requires the cost calculation inside any above-cap finding to account for reductions in permitting and inspection costs achieved under 65850.5. The statute anticipates automation lowering costs and asks jurisdictions to show their work on it. A jurisdiction that adopted SolarAPP+ under SB 379 and then claims high plan review cost is arguing against the exact provision written to catch that.</p>

    <p>Inspection is where the defensible money sits. Expect fee schedules to keep drifting that direction as automated permitting spreads.</p>

    <h3>4. Line items are multiplying</h3>

    <p>Section 65850.55 requires every fee to appear separately on the applicant's invoice, and 66015(e) caps the sum. Together those push jurisdictions toward publishing more components.</p>

    <p>Concord shows the mechanism most clearly. The 2026 schedule splits into a $70 administrative fee, $115 plan review for systems up to 15 kW, and a $265 inspection base fee. That sums to $450.00.</p>

    <p>Santa Rosa publishes plan check and inspection separately. Fairfield consolidated in the other direction, and also split roof mount from ground mount for the first time in 2026, at $157 and $328.</p>

    <p>The practical consequence: a headline permit fee no longer tells you what a job costs. Fairfield's published solar fee moved from $72.74 to $157, which reads as a 116% increase and was actually an 18% decrease once the 2025 inspection charge is included.</p>

    <h2>Where the numbers sit relative to the cap</h2>

    <p>Two of the nine meaningfully exceed $450 at 10 kW, a size every jurisdiction here covers with a published base fee: San Ramon at $566 and Solano County at $581 for ground mount. Each has documented cost supporting the fee, and Solano's traces specifically to inspection cost.</p>

    <p>That's the practical takeaway for anyone pricing California work. The $450 figure is a reasonable default assumption and a poor universal one. A jurisdiction that completes the 66015(c) findings process can sit above the cap legally, and several do.</p>

    <p><strong>And the biggest takeaway?</strong> Get it right from the start. Most of these fees include a set number of inspections or permit reviews. One, two, whatever the department decided. Go past it and you pay again. That's where the fee schedule stops being the whole story, and it's why the number in the table is a floor rather than a price. Get it right the first time.</p>
  `,
  coverImage: coverImage,
  date: '2026-08-06T12:00:00',
  readTime: 8,
  categories: ['Clean Energy', 'Permitting'],
  author: {
    name: 'Gillian Sowray',
    title: 'CEO & Co-Founder',
    avatar: '/lovable-uploads/69c2731c-f0dc-41d3-8812-8b392d7302c2.png',
  },
  metaDescription:
    "California caps residential solar permit fees at $450. We compared 2025/26 and 2026/27 fee schedules across nine jurisdictions — here's what changed.",
  seoKeywords: [
    'California solar permit fees',
    'Government Code 66015',
    '$450 solar permit cap',
    'residential storage permit fees',
    'SolarAPP+',
    '2026 fee schedule',
  ],
};
